import axios from "axios";
import { apiSSO } from "./api-client";

/**
 * @interface PermisosUsuarioDTO
 * @description Representa los permisos asignados a un usuario para una sección específica del sistema.
 * Cada objeto DTO corresponde a una sección con sus respectivas claves de actividad permitidas.
 */
export interface PermisosUsuarioDTO
{
  /**
   * @property {number} IdUsuario - Identificador único del usuario al que pertenecen los permisos.
   */
  IdUsuario: number;
  /**
   * @property {string} NombreRol - Nombre del rol asignado al usuario (ej. "Operativo", "Empresa").
   */
  NombreRol: string;
  /**
   * @property {string} ClaveSeccion - Clave identificadora de la sección habilitada para el usuario
   * (ej. "ListadoFacturas", "GestionarTipoDocumento").
   */
  ClaveSeccion: string;
  /**
   * @property {string[]} ClavesActividad - Lista de claves de actividades permitidas dentro de la sección
   * (ej. ["VerFacturas", "CargarFactura", "EliminarArchivo"]).
   */
  ClavesActividad: string[];
}

/**
 * @function getPermissions
 * @description Obtiene los permisos de secciones y actividades de un usuario desde el endpoint SSO.
 * Cada elemento del array retornado representa una sección habilitada con sus actividades permitidas.
 * @param {number} IdUsuario - Identificador único del usuario cuyos permisos se desean consultar.
 * @param {AbortSignal} [signal] - Cancela el pedido (el store lo usa para no esperar sin límite).
 * @returns {Promise<PermisosUsuarioDTO[]>} Promesa que se resuelve con el array de permisos del usuario.
 * @throws {Error} Si el usuario no es encontrado, no tiene permisos o falla la conexión con el servidor.
 */
export async function getPermissions(IdUsuario: number, signal?: AbortSignal): Promise<PermisosUsuarioDTO[]> {
  try {
    const response = await apiSSO.get(`/Permisos/ObtenerPermisosxIDUsuario/${IdUsuario}`, { signal });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(
        `Error de API en getPermissions: ${error.response?.status} - ${error.response?.data}`,
        error
      );
      throw new Error(
        `Error ${error.response?.status}: No se pudieron obtener los Permisos.`
      );
    } else {
      console.error('Error al obtener los Permisos:', error);
      throw new Error('Error inesperado al obtener los Permisos.');
    }
  }
}

